import { NodeTemplate } from 'models/nodes/NodeTemplate';
import { NodeType } from 'models/nodes/NodeType';
import { ConditionFieldDefinition } from 'models/workflows/ConditionField';
import { ApiService } from 'services/ApiService';

/**
 * Returns the list of condition fields available on the output of the given
 * node. These values populate the left-hand-side dropdown in the edge
 * condition builder.
 *
 * - TeamReview  → hardcoded Decision (enum) + Notes (string); no API call.
 * - AutomatedAction → empty; no API call.
 * - Form → loads the form via the team-scoped API and maps each non-title element.
 *
 * `teamId` is required for Form nodes so the call can be routed through the
 * existing team-scoped FormsController endpoint.
 *
 * Never throws — on any failure the function returns [].
 */
export async function getNodeConditionFields(
  node: NodeTemplate,
  teamId: string,
): Promise<ConditionFieldDefinition[]> {
  switch (node.nodeType) {
    case NodeType.TeamReview:
      return [
        {
          key: 'Decision',
          label: 'Decision',
          type: 'enum',
          enumValues: ['Approved', 'RequestChanges', 'Rejected'],
        },
        { key: 'Notes', label: 'Notes', type: 'string' },
      ];

    case NodeType.AutomatedAction:
      return [];

    case NodeType.Form: {
      // --- Extract formId from configurationJson ---
      let formId: string | undefined;
      try {
        const config = JSON.parse(node.configurationJson ?? '');
        formId = typeof config?.formId === 'string' ? config.formId : undefined;
      } catch {
        return [];
      }

      if (!formId) {
        return [];
      }

      // --- Fetch form and map elements ---
      try {
        const response = await ApiService.getForm(teamId, formId);
        const form = response.data;
        if (!form) return [];

        return form.elements
          .filter((el) => el.index !== -2) // exclude Title elements
          .sort((a, b) => a.index - b.index)
          .map((el): ConditionFieldDefinition => {
            const typeStr = el.type ?? '';
            // options is present on RadioButtonListElement / CheckboxListElement
            const options = (el as unknown as { options?: string[] }).options;

            if (
              typeStr.includes('RadioButton') ||
              typeStr.includes('CheckBox') ||
              typeStr.includes('Dropdown') ||
              typeStr.includes('MultipleChoice')
            ) {
              return {
                key: el.name,
                label: el.name,
                type: 'enum',
                enumValues: Array.isArray(options) ? options : [],
              };
            }

            if (typeStr.includes('Number')) {
              return { key: el.name, label: el.name, type: 'number' };
            }

            return { key: el.name, label: el.name, type: 'string' };
          });
      } catch {
        return [];
      }
    }

    default:
      return [];
  }
}
